import React, { useMemo } from 'react';
import { StyleSheet, Switch, Text, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Step 3: Location education (US2, T044). Names the current real location,
 * identifies the Home-header location picker and its data-switch
 * consequences, and offers an opt-in test-location preference (off by default).
 */

export interface LocationEducationStepProps {
  /** Verified real current location name; empty while still loading. */
  currentLocationName: string;
  locationLoading?: boolean;
  /** Opt-in for test location; defaults to false in the wizard host. */
  testLocationEnabled?: boolean;
  onTestLocationEnabledChange?: (enabled: boolean) => void;
  /** Disable the switch while the preference is being saved. */
  testLocationPreferenceBusy?: boolean;
}

const LocationEducationStep: React.FC<LocationEducationStepProps> = ({
  currentLocationName,
  locationLoading = false,
  testLocationEnabled = false,
  onTestLocationEnabledChange,
  testLocationPreferenceBusy = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const trimmedName = String(currentLocationName || '').trim();
  const switchDisabled =
    testLocationPreferenceBusy || typeof onTestLocationEnabledChange !== 'function';

  return (
    <View style={styles.container}>
      <View
        style={styles.currentLocationCard}
        accessible
        accessibilityLabel={
          trimmedName
            ? `Your current location is ${trimmedName}`
            : 'Your current location is still loading'
        }>
        <View style={styles.locationIcon}>
          <Ionicons
            name="location-outline"
            size={iconSize(20)}
            color={theme.colors.primary}
          />
        </View>
        <View style={styles.locationText}>
          <Text style={styles.locationEyebrow}>You are working in</Text>
          <Text style={styles.locationName} testID="location-current-name">
            {trimmedName || (locationLoading ? 'Loading…' : 'Your location')}
          </Text>
        </View>
      </View>

      <Text style={styles.body}>
        The location picker at the top of Home Screen shows which business location you are
        in. Tap it to create new locations and switch to them.
      </Text>

      <View
        style={styles.testCallout}
        accessible={false}
        testID="location-test-callout">
        <View style={styles.testCalloutHeader}>
          <Ionicons
            name="flask-outline"
            size={iconSize(20)}
            color={theme.colors.warningDark}
          />
          <Text style={styles.testCalloutTitle}>A safe place to explore</Text>
        </View>
        <Text style={styles.testCalloutBody}>
          A test location is available when you want a safe place to test out BizPlus features first.
          Activity there does not affect your real location.
        </Text>
        <View style={styles.switchField}>
          <View style={styles.switchText}>
            <Text style={styles.switchTitle}>Enable test location</Text>
            <Text style={styles.switchSubtitle}>
              Turn this on if you want a test location to try things out.
            </Text>
          </View>
          <Switch
            testID="location-test-location-switch"
            trackColor={{
              false: theme.colors.textSecondary,
              true: theme.colors.primary,
            }}
            thumbColor={theme.colors.surface}
            ios_backgroundColor={theme.colors.text}
            onValueChange={onTestLocationEnabledChange}
            value={Boolean(testLocationEnabled)}
            disabled={switchDisabled}
            accessibilityLabel="Enable test location"
          />
        </View>
      </View>
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      gap: v(16),
    },
    currentLocationCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderSecondary,
      padding: s(16),
      gap: s(12),
    },
    locationIcon: {
      width: iconSize(40),
      height: iconSize(40),
      borderRadius: iconSize(20),
      backgroundColor: withOpacity(theme.colors.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    locationText: {
      flex: 1,
      gap: v(2),
    },
    locationEyebrow: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    locationName: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(21),
      color: theme.colors.text,
    },
    body: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(21),
      color: theme.colors.text,
      marginBottom: v(12),
    },
    testCallout: {
      backgroundColor: withOpacity(theme.colors.warning, 0.1),
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: withOpacity(theme.colors.warning, 0.4),
      padding: s(16),
      gap: v(8),
    },
    testCalloutHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    testCalloutTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    testCalloutBody: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(19),
      color: theme.colors.text,
    },
    switchField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      marginTop: v(4),
      paddingTop: v(8),
      borderTopWidth: borderWidth(1),
      borderTopColor: withOpacity(theme.colors.warning, 0.35),
    },
    switchText: {
      flex: 1,
      gap: v(4),
    },
    switchTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    switchSubtitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textSecondary,
    },
  });

export default LocationEducationStep;
